import { useCallback, useEffect, useId, useRef, useState } from 'react';
import { AnimatePresence, motion } from 'framer-motion';
import {
  ArrowLeft,
  ArrowRight,
  CheckCircle2,
  Clock,
  Package,
} from 'lucide-react';
import { Container } from '../layout/Container';
import { Reveal } from '../motion/Reveal';
import { Button } from '../ui/button';
import { getIcon } from '../../lib/lucide-icons';
import { cn } from '../../lib/cn';
import { easeStripe } from '../../lib/motion';

export interface JourneyStepData {
  position: number;
  title: string;
  description: string;
  duration?: string | null;
  iconName: string;
  deliverable?: string | null;
  checklist?: string[];
}

export interface JourneyTimelineProps {
  eyebrow: string;
  title: string;
  description: string;
  totalDuration?: string | null;
  ctaLabel?: string | null;
  ctaHref?: string | null;
  steps: JourneyStepData[];
  /** Couleur d'accent (selon audience) */
  tone?: 'brand' | 'lavender' | 'sage' | 'lavender-700' | 'brand-700' | 'accent';
}

interface ToneStyles {
  iconBg: string;
  iconText: string;
  textActive: string;
  ringActive: string;
  badgeBg: string;
  dot: string;
  rail: string;
  bullet: string;
}

const TONES: Record<NonNullable<JourneyTimelineProps['tone']>, ToneStyles> = {
  brand: {
    iconBg: 'bg-brand-50',
    iconText: 'text-brand-700',
    textActive: 'text-brand-700',
    ringActive: 'ring-brand-300/70',
    badgeBg: 'bg-brand-50 text-brand-700 ring-brand-200/50',
    dot: 'bg-brand-600',
    rail: 'bg-brand-600',
    bullet: 'bg-brand-500',
  },
  lavender: {
    iconBg: 'bg-lavender-50',
    iconText: 'text-lavender-600',
    textActive: 'text-lavender-700',
    ringActive: 'ring-lavender-300/70',
    badgeBg: 'bg-lavender-50 text-lavender-700 ring-lavender-200/50',
    dot: 'bg-lavender-600',
    rail: 'bg-lavender-600',
    bullet: 'bg-lavender-500',
  },
  sage: {
    iconBg: 'bg-sage-100',
    iconText: 'text-sage-500',
    textActive: 'text-sage-600',
    ringActive: 'ring-sage-200',
    badgeBg: 'bg-sage-100 text-sage-500 ring-sage-200/60',
    dot: 'bg-sage-500',
    rail: 'bg-sage-500',
    bullet: 'bg-sage-500',
  },
  'lavender-700': {
    iconBg: 'bg-lavender-50',
    iconText: 'text-lavender-700',
    textActive: 'text-lavender-800',
    ringActive: 'ring-lavender-300',
    badgeBg: 'bg-lavender-50 text-lavender-800 ring-lavender-300/50',
    dot: 'bg-lavender-700',
    rail: 'bg-lavender-700',
    bullet: 'bg-lavender-600',
  },
  'brand-700': {
    iconBg: 'bg-brand-50',
    iconText: 'text-brand-800',
    textActive: 'text-brand-800',
    ringActive: 'ring-brand-300',
    badgeBg: 'bg-brand-50 text-brand-800 ring-brand-300/50',
    dot: 'bg-brand-700',
    rail: 'bg-brand-700',
    bullet: 'bg-brand-600',
  },
  accent: {
    iconBg: 'bg-accent-50',
    iconText: 'text-accent-700',
    textActive: 'text-accent-800',
    ringActive: 'ring-accent-300/70',
    badgeBg: 'bg-accent-50 text-accent-800 ring-accent-200/50',
    dot: 'bg-accent-600',
    rail: 'bg-accent-600',
    bullet: 'bg-accent-500',
  },
};

export function JourneyTimeline({
  eyebrow,
  title,
  description,
  totalDuration,
  ctaLabel,
  ctaHref,
  steps,
  tone = 'brand',
}: JourneyTimelineProps) {
  const t = TONES[tone];
  const [active, setActive] = useState(0);
  const uid = useId();
  const railRef = useRef<HTMLDivElement>(null);
  const pillsRef = useRef<HTMLDivElement>(null);
  const activePillRef = useRef<HTMLButtonElement>(null);

  const total = steps.length;
  const step = steps[active];

  const go = useCallback(
    (dir: -1 | 1) => {
      setActive((prev) => {
        const next = prev + dir;
        if (next < 0) return 0;
        if (next > total - 1) return total - 1;
        return next;
      });
    },
    [total],
  );

  useEffect(() => {
    if (!activePillRef.current || !pillsRef.current) return;
    activePillRef.current.scrollIntoView({
      behavior: 'smooth',
      block: 'nearest',
      inline: 'center',
    });
  }, [active]);

  if (!step) return null;

  const progress = total > 1 ? (active / (total - 1)) * 100 : 100;

  return (
    <section className="bg-paper py-14 md:py-20">
      <Container className="max-w-6xl">
        {/* Header */}
        <Reveal>
          <div className="mb-10 md:mb-12">
            <p className="eyebrow">{eyebrow}</p>
            <div className="mt-3 flex flex-wrap items-end justify-between gap-4">
              <h2 className="max-w-2xl font-display text-2xl font-bold tracking-tight text-balance text-ink md:text-[28px]">
                {title}
              </h2>
              {totalDuration && (
                <span
                  className={cn(
                    'inline-flex shrink-0 items-center gap-1.5 rounded-md px-2 py-1 text-[11px] font-semibold ring-1 ring-inset',
                    t.badgeBg,
                  )}
                >
                  <Clock className="h-3 w-3" strokeWidth={2} />
                  Délai global, {totalDuration}
                </span>
              )}
            </div>
            <p className="mt-3 max-w-2xl text-[15px] text-stone-600 leading-relaxed">
              {description}
            </p>
          </div>
        </Reveal>

        {/* ================================== */}
        {/* MOBILE : pills horizontales + détail */}
        {/* ================================== */}
        <div className="lg:hidden">
          <div className="relative">
            {/* Fades latéraux : suggèrent le contenu scrollable hors écran */}
            <div
              aria-hidden
              className="pointer-events-none absolute inset-y-0 -left-4 z-10 w-7 bg-gradient-to-r from-paper to-transparent"
            />
            <div
              aria-hidden
              className="pointer-events-none absolute inset-y-0 -right-4 z-10 w-7 bg-gradient-to-l from-paper to-transparent"
            />
            <div
              ref={pillsRef}
              role="tablist"
              tabIndex={0}
              aria-label="Étapes du parcours"
              className="-mx-4 flex snap-x snap-mandatory gap-2 overflow-x-auto scroll-smooth px-4 pb-3 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-500/30 focus-visible:ring-offset-2 focus-visible:ring-offset-paper rounded-md [scrollbar-width:none] [&::-webkit-scrollbar]:hidden"
              onKeyDown={(e) => {
                if (e.key === 'ArrowRight') {
                  e.preventDefault();
                  go(1);
                } else if (e.key === 'ArrowLeft') {
                  e.preventDefault();
                  go(-1);
                }
              }}
            >
              {steps.map((s, idx) => {
                const isActive = idx === active;
                return (
                  <button
                    key={s.position}
                    ref={isActive ? activePillRef : undefined}
                    type="button"
                    role="tab"
                    aria-selected={isActive}
                    aria-controls={`${uid}-panel`}
                    onClick={() => setActive(idx)}
                    className={cn(
                      'group relative flex shrink-0 snap-start items-center gap-2 rounded-full border border-stone-200/70 bg-paper px-3 py-1.5 text-left transition-all duration-200 ease-out',
                      isActive
                        ? cn('ring-2 ring-inset shadow-soft', t.ringActive)
                        : 'hover:border-stone-300 hover:bg-stone-50/60',
                    )}
                  >
                    <span
                      className={cn(
                        'font-mono text-[10px] font-bold',
                        isActive ? t.textActive : 'text-stone-400',
                      )}
                    >
                      {String(s.position).padStart(2, '0')}
                    </span>
                    <span
                      className={cn(
                        'text-[12px] font-medium whitespace-nowrap',
                        isActive ? 'text-ink' : 'text-stone-600',
                      )}
                    >
                      {s.title}
                    </span>
                  </button>
                );
              })}
            </div>
          </div>

          {/* Barre de progression mobile */}
          <div className="mb-5 mt-1 h-1 w-full overflow-hidden rounded-full bg-stone-100">
            <motion.div
              className={cn('h-full rounded-full', t.rail)}
              initial={false}
              animate={{ width: `${progress}%` }}
              transition={{ duration: 0.4, ease: easeStripe }}
            />
          </div>

          {/* Panneau détail mobile */}
          <JourneyStepDetail
            step={step}
            tone={tone}
            active={active}
            total={total}
            onPrev={() => go(-1)}
            onNext={() => go(1)}
            panelId={`${uid}-panel`}
          />
        </div>

        {/* ================================== */}
        {/* DESKTOP : split 2 colonnes          */}
        {/* ================================== */}
        <div className="hidden lg:grid lg:grid-cols-[minmax(280px,320px)_1fr] lg:gap-8 xl:gap-12">
          {/* Colonne gauche : stepper vertical */}
          <div
            ref={railRef}
            role="tablist"
            tabIndex={0}
            aria-label="Étapes du parcours"
            aria-orientation="vertical"
            className="relative focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-500/30 focus-visible:ring-offset-2 focus-visible:ring-offset-paper rounded-md"
            onKeyDown={(e) => {
              if (e.key === 'ArrowDown') {
                e.preventDefault();
                go(1);
              } else if (e.key === 'ArrowUp') {
                e.preventDefault();
                go(-1);
              }
            }}
          >
            {/* Ligne connectrice — fond */}
            <div
              aria-hidden
              className="absolute left-[1.125rem] top-5 bottom-5 w-px bg-stone-200"
            />
            {/* Ligne connectrice — progress */}
            <motion.div
              aria-hidden
              initial={false}
              animate={{ height: `calc(${progress}% + 0.5rem)` }}
              transition={{ duration: 0.45, ease: easeStripe }}
              className={cn(
                'absolute left-[1.125rem] top-5 w-px origin-top',
                t.rail,
              )}
            />

            <ol className="relative space-y-1">
              {steps.map((s, idx) => {
                const isActive = idx === active;
                const isPast = idx < active;
                const Icon = getIcon(s.iconName);
                return (
                  <li key={s.position} className="relative">
                    <button
                      type="button"
                      role="tab"
                      aria-selected={isActive}
                      aria-controls={`${uid}-panel`}
                      tabIndex={isActive ? 0 : -1}
                      onClick={() => setActive(idx)}
                      className={cn(
                        'group relative flex w-full items-center gap-3 rounded-lg py-2.5 pl-1 pr-3 text-left transition-all duration-200 ease-out',
                        isActive
                          ? 'bg-stone-50/70'
                          : 'hover:bg-stone-50/50',
                      )}
                    >
                      {/* Pastille — numéro + icône au changement */}
                      <span
                        className={cn(
                          'relative z-10 flex h-9 w-9 shrink-0 items-center justify-center rounded-full border ring-4 ring-paper transition-all duration-200 ease-out',
                          isActive
                            ? cn('border-transparent', t.iconBg)
                            : isPast
                              ? cn('border-transparent', t.iconBg, 'opacity-80')
                              : 'border-stone-200 bg-paper',
                        )}
                      >
                        {isActive ? (
                          <Icon
                            className={cn('h-4 w-4', t.iconText)}
                            strokeWidth={1.8}
                          />
                        ) : isPast ? (
                          <CheckCircle2
                            className={cn('h-3.5 w-3.5', t.iconText)}
                            strokeWidth={2}
                          />
                        ) : (
                          <span className="font-mono text-[10px] font-bold text-stone-400">
                            {String(s.position).padStart(2, '0')}
                          </span>
                        )}
                        {isActive && (
                          <motion.span
                            layoutId="journey-active-ring"
                            className={cn(
                              'absolute -inset-0.5 rounded-full ring-2',
                              t.ringActive,
                            )}
                            transition={{ duration: 0.3, ease: easeStripe }}
                          />
                        )}
                      </span>

                      {/* Titre + durée */}
                      <div className="min-w-0 flex-1">
                        <div className="flex items-baseline justify-between gap-2">
                          <span
                            className={cn(
                              'font-mono text-[10px] font-bold tracking-wider uppercase transition-colors duration-200',
                              isActive
                                ? t.textActive
                                : isPast
                                  ? 'text-stone-500'
                                  : 'text-stone-400',
                            )}
                          >
                            Étape {String(s.position).padStart(2, '0')}
                          </span>
                          {s.duration && (
                            <span
                              className={cn(
                                'font-mono text-[10px] text-stone-400 transition-colors duration-200',
                                isActive && 'text-stone-500',
                              )}
                            >
                              {s.duration}
                            </span>
                          )}
                        </div>
                        <p
                          className={cn(
                            'mt-0.5 truncate font-display text-[13px] font-semibold leading-tight transition-colors duration-200',
                            isActive
                              ? 'text-ink'
                              : isPast
                                ? 'text-stone-600'
                                : 'text-stone-500',
                          )}
                        >
                          {s.title}
                        </p>
                      </div>
                    </button>
                  </li>
                );
              })}
            </ol>
          </div>

          {/* Colonne droite : panneau détail */}
          <div className="min-w-0">
            <JourneyStepDetail
              step={step}
              tone={tone}
              active={active}
              total={total}
              onPrev={() => go(-1)}
              onNext={() => go(1)}
              panelId={`${uid}-panel`}
            />
          </div>
        </div>

        {/* Footer CTA */}
        {ctaLabel && ctaHref && (
          <Reveal>
            <div className="mt-10 flex justify-center md:mt-12">
              <Button asChild size="lg" variant="primary" className="group">
                <a href={ctaHref}>
                  {ctaLabel}
                  <ArrowRight className="h-3.5 w-3.5 transition-transform duration-150 ease-out group-hover/btn:translate-x-0.5" />
                </a>
              </Button>
            </div>
          </Reveal>
        )}
      </Container>
    </section>
  );
}

function JourneyStepDetail({
  step,
  tone,
  active,
  total,
  onPrev,
  onNext,
  panelId,
}: {
  step: JourneyStepData;
  tone: NonNullable<JourneyTimelineProps['tone']>;
  active: number;
  total: number;
  onPrev: () => void;
  onNext: () => void;
  panelId: string;
}) {
  const t = TONES[tone];
  const Icon = getIcon(step.iconName);
  const hasChecklist = !!(step.checklist && step.checklist.length > 0);
  const hasDeliverable = !!step.deliverable;

  return (
    <div
      id={panelId}
      role="tabpanel"
      aria-live="polite"
      className="relative overflow-hidden rounded-2xl border border-stone-200/70 bg-paper shadow-[0_1px_2px_rgba(10,10,10,.04)]"
    >
      <AnimatePresence mode="wait" initial={false}>
        <motion.div
          key={step.position}
          initial={{ opacity: 0, y: 8 }}
          animate={{ opacity: 1, y: 0 }}
          exit={{ opacity: 0, y: -6 }}
          transition={{ duration: 0.28, ease: easeStripe }}
          className="p-6 md:p-8"
        >
          {/* En-tête */}
          <div className="flex items-start gap-4">
            <div
              className={cn(
                'flex h-12 w-12 shrink-0 items-center justify-center rounded-xl',
                t.iconBg,
              )}
            >
              <Icon className={cn('h-5 w-5', t.iconText)} strokeWidth={1.7} />
            </div>
            <div className="min-w-0 flex-1">
              <div className="flex flex-wrap items-center gap-x-3 gap-y-1">
                <span
                  className={cn(
                    'font-mono text-[10px] font-bold uppercase tracking-[0.16em]',
                    t.textActive,
                  )}
                >
                  Étape {String(step.position).padStart(2, '0')} / {String(total).padStart(2, '0')}
                </span>
                {step.duration && (
                  <span className="inline-flex items-center gap-1 rounded-md bg-stone-100 px-1.5 py-0.5 text-[10px] font-medium text-stone-600">
                    <Clock className="h-2.5 w-2.5" strokeWidth={2.2} />
                    {step.duration}
                  </span>
                )}
              </div>
              <h3 className="mt-1.5 font-display text-xl font-bold leading-tight tracking-tight text-balance text-ink md:text-2xl">
                {step.title}
              </h3>
            </div>
          </div>

          {/* Description */}
          <p className="mt-5 text-[15px] leading-relaxed text-stone-600">
            {step.description}
          </p>

          {/* Livrable + Checklist */}
          {(hasDeliverable || hasChecklist) && (
            <div className="mt-6 grid gap-4 rounded-xl border border-stone-200/70 bg-stone-50/40 p-5 md:grid-cols-[minmax(180px,220px)_1fr]">
              {hasDeliverable && (
                <div>
                  <p className="inline-flex items-center gap-1.5 text-[10px] font-semibold uppercase tracking-[0.14em] text-stone-500">
                    <Package
                      className={cn('h-3 w-3', t.iconText)}
                      strokeWidth={2}
                    />
                    Livrable
                  </p>
                  <p className="mt-2 text-sm font-semibold leading-snug text-ink">
                    {step.deliverable}
                  </p>
                </div>
              )}
              {hasChecklist && (
                <div className={cn(hasDeliverable && 'md:border-l md:border-stone-200/70 md:pl-5')}>
                  <p className="inline-flex items-center gap-1.5 text-[10px] font-semibold uppercase tracking-[0.14em] text-stone-500">
                    <CheckCircle2
                      className={cn('h-3 w-3', t.iconText)}
                      strokeWidth={2}
                    />
                    Checklist
                  </p>
                  <ul className="mt-2 grid gap-1.5 text-sm text-stone-700 sm:grid-cols-2 md:grid-cols-1 md:gap-1">
                    {step.checklist!.map((item) => (
                      <li key={item} className="flex items-start gap-2">
                        <span
                          className={cn(
                            'mt-1.5 h-1 w-1 shrink-0 rounded-full',
                            t.bullet,
                          )}
                        />
                        <span className="leading-snug">{item}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              )}
            </div>
          )}
        </motion.div>
      </AnimatePresence>

      {/* Footer navigation */}
      <div className="flex items-center justify-between border-t border-stone-200/60 bg-stone-50/40 px-6 py-3 md:px-8">
        <button
          type="button"
          onClick={onPrev}
          disabled={active === 0}
          className={cn(
            'inline-flex items-center gap-1.5 rounded-md px-2 py-1 text-[12px] font-medium transition-all duration-150 ease-out',
            active === 0
              ? 'cursor-not-allowed text-stone-300'
              : 'text-stone-600 hover:bg-stone-100 hover:text-ink',
          )}
          aria-label="Étape précédente"
        >
          <ArrowLeft className="h-3 w-3" strokeWidth={2} />
          Précédent
        </button>
        <span className="font-mono text-[11px] text-stone-400 tabular-nums">
          {String(active + 1).padStart(2, '0')} / {String(total).padStart(2, '0')}
        </span>
        <button
          type="button"
          onClick={onNext}
          disabled={active === total - 1}
          className={cn(
            'inline-flex items-center gap-1.5 rounded-md px-2 py-1 text-[12px] font-medium transition-all duration-150 ease-out',
            active === total - 1
              ? 'cursor-not-allowed text-stone-300'
              : 'text-stone-600 hover:bg-stone-100 hover:text-ink',
          )}
          aria-label="Étape suivante"
        >
          Suivant
          <ArrowRight className="h-3 w-3" strokeWidth={2} />
        </button>
      </div>
    </div>
  );
}
